import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ParallaxBackground from '../components/ParallaxBackground';
import { ChevronDown } from 'lucide-react';

const EFFECTIVE_DATE = 'January 1, 2026';

const SECTIONS = [
  {
    title: '1. Information We Collect',
    body: `When you use Petfolk, we collect:\n\n• Account information: name, phone number, email address\n• Pet profiles: pet name, breed, age, weight, vaccination records, special care notes\n• Booking data: services booked, store visited, session notes, before/after grooming photos\n• Payment information: transaction IDs (we do not store full card details — payments are processed by Razorpay)\n• Usage data: pages visited, device type, browser, IP address, session duration\n• Communications: support tickets, WhatsApp messages, and feedback you provide`,
  },
  {
    title: '2. How We Use Your Information',
    body: `We use your information to:\n\n• Process and manage your bookings and payments\n• Personalise your grooming history and service recommendations\n• Send booking confirmations, reminders, and service updates via SMS, WhatsApp, or email\n• Manage your Paws Credits, subscriptions, and referral rewards\n• Improve our services, troubleshoot issues, and prevent fraud\n• Comply with legal obligations under Indian law`,
  },
  {
    title: '3. Pet Data & Grooming Records',
    body: `Pet profiles, health notes, and grooming photos (before/after) are stored securely and accessible only to you and the Petfolk team servicing your pet. Grooming photos may be used with your consent for quality review. We do not sell or share your pet's data with third parties for marketing purposes.`,
  },
  {
    title: '4. Live Streaming',
    body: `When you opt into live streaming of a grooming session, the stream is delivered in real-time to your authenticated Paws account only. Petfolk does not record, store, or archive live stream footage. Stream access tokens expire immediately after the session ends.`,
  },
  {
    title: '5. Data Sharing',
    body: `We do not sell your personal data. We share data only:\n\n• With franchise partners operating your selected store (limited to booking details needed for service)\n• With payment processors (Razorpay) for transaction processing\n• With communication providers (SMS/WhatsApp) for notifications\n• With analytics tools to improve our platform (anonymised)\n• When required by law or court order`,
  },
  {
    title: '6. Data Retention',
    body: `We retain your account and booking data for as long as your account is active and for up to 3 years thereafter for legal and audit purposes. Grooming session photos are retained for 12 months. You may request deletion of your data at any time by writing to support@petfolk.app.`,
  },
  {
    title: '7. Cookies & Tracking',
    body: `Petfolk.in uses strictly necessary cookies for session management and authentication. We do not use third-party advertising cookies or sell behavioural data to ad networks. Analytics cookies (if used) are anonymised and aggregate-only.`,
  },
  {
    title: '8. Data Security',
    body: `We implement industry-standard security measures including HTTPS encryption, JWT-based authentication, and role-based access control. Payment data is handled exclusively by Razorpay, which is PCI DSS compliant. In the event of a data breach that affects your data, we will notify you within 72 hours as required by applicable law.`,
  },
  {
    title: '9. Your Rights',
    body: `Under applicable Indian data protection laws, you have the right to:\n\n• Access a copy of the personal data we hold about you\n• Correct inaccurate data\n• Request deletion of your account and associated data\n• Opt out of marketing communications at any time\n• Withdraw consent for WhatsApp notifications\n\nTo exercise any of these rights, contact support@petfolk.app.`,
  },
  {
    title: '10. Children\'s Privacy',
    body: `Petfolk services are intended for adults aged 18 and above. We do not knowingly collect personal information from minors. If you believe a minor has registered on our platform, please contact support@petfolk.app and we will delete the account promptly.`,
  },
  {
    title: '11. Changes to This Policy',
    body: `We may update this Privacy Policy from time to time. The revised version will be posted at petfolk.in/privacy with an updated effective date. Continued use of our services after any changes constitutes acceptance. For material changes, we will notify you via the Paws app or email.`,
  },
  {
    title: '12. Contact Us',
    body: `For privacy-related questions or requests:\n\nEmail: support@petfolk.app\nPhone / WhatsApp: +91 9160153336\nHours: Monday – Sunday, 9 AM – 9 PM IST`,
  },
];

export default function PrivacyPage() {
  const [open, setOpen] = useState(null);

  return (
    <div className="relative min-h-screen pt-20" style={{ background: 'var(--bg-primary)' }}>
      <ParallaxBackground />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 relative z-10">
        <div className="text-center mb-10">
          <h1 className="font-outfit text-3xl sm:text-4xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
            Privacy <span className="gradient-text">Policy</span>
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Effective date: {EFFECTIVE_DATE}</p>
        </div>

        <div className="space-y-3">
          {SECTIONS.map((s, i) => (
            <div key={i} className="petfolk-card overflow-hidden">
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full px-6 py-4 flex items-center justify-between text-left"
              >
                <span className="font-outfit font-semibold" style={{ color: 'var(--text-primary)' }}>{s.title}</span>
                <ChevronDown size={16} className={`transition-transform shrink-0 ml-4 ${open === i ? 'rotate-180' : ''}`} style={{ color: 'var(--text-secondary)' }} />
              </button>
              {open === i && (
                <div className="px-6 pb-5 text-sm leading-relaxed whitespace-pre-line" style={{ color: 'var(--text-secondary)' }}>
                  {s.body}
                </div>
              )}
            </div>
          ))}
        </div>

        <p className="mt-10 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
          Read our{' '}
          <Link to="/tnc" className="text-petfolk-orange hover:underline">Terms &amp; Conditions</Link>
          {' '}·{' '}
          <Link to="/support" className="text-petfolk-orange hover:underline">Contact Support</Link>
        </p>
      </div>
    </div>
  );
}
